/**
 * dislocationFieldLogos — animated crystallographic defects with
 * social-media logos stamped into each grain's dislocation core.
 *
 * Copied from `dislocationFieldPipeline.ts` and modified to render
 * a small logo (Facebook, Instagram, X) inside each of the 36 grain
 * cores. The logo rotates slowly with the lattice orientation so it
 * reads as a "tag" embedded in the crystal.
 *
 * The original `dislocationField` scene is untouched — this is a
 * separate pipeline + scene.
 */
import { linkProgram, POST_ATTRIBS } from "./glContext";

export interface DislocationFieldLogosPipeline {
  program: WebGLProgram;
  vao: WebGLVertexArrayObject | null;
  vbo: WebGLBuffer | null;
  texFb: WebGLTexture;
  texIg: WebGLTexture;
  texX: WebGLTexture;
  bands: Float32Array;
}

const VERT_SRC = `#version 300 es
precision highp float; layout(location=0) in vec2 aPos; out vec2 vUV;
void main(){ vUV = aPos*0.5+0.5; gl_Position = vec4(aPos, 0.0, 1.0); }`;

const FRAG_SRC = `#version 300 es
precision highp float; in vec2 vUV; out vec4 outColor;
uniform vec2 uResolution; uniform float uTime;
uniform vec3 uBass, uMid, uHigh;
uniform sampler2D uTexFb;
uniform sampler2D uTexIg;
uniform sampler2D uTexX;
float bass; float mid; float high;

const float TAU = 6.283185307179586;
const float PI = 3.14159265358979;

float hash11(float n) { return fract(sin(n * 12.9898) * 43758.5453); }
float hash21(vec2 p) {
  p = fract(p * vec2(123.34, 456.21));
  p += dot(p, p + 45.32);
  return fract(p.x * p.y);
}

const int N_GRAINS = 36;

void grain(int i, float t, out vec2 pos, out float angle, out float charge) {
  float fi = float(i);
  float ph = fi * 2.39996;
  float rad = sqrt(fi / float(N_GRAINS)) * 1.2;
  pos = vec2(cos(ph), sin(ph)) * rad;
  float pre = t * 0.04;
  pos = vec2(pos.x * cos(pre) - pos.y * sin(pre), pos.x * sin(pre) + pos.y * cos(pre));
  angle = hash11(fi * 5.41) * TAU + t * (0.04 + 0.03 * hash11(fi * 7.93));
  float mod3 = mod(fi, 3.0);
  charge = (mod3 < 0.5) ? 1.0 : (mod3 < 1.5 ? -1.0 : 0.0);
}

vec3 iridescent(float t) {
  return 0.5 + 0.5 * cos(6.28318530718 * (t + vec3(0.0, 0.33, 0.67)));
}

// Pick one of the 3 logo textures based on grain index, then sample it
// at the given UV. GLSL ES 3.0 doesn't allow sampler2D as a function
// return, so the pick happens inline in the call site.
vec4 sampleLogo(int i, vec2 logoUV) {
  int m = i - 3 * (i / 3);
  if (m == 0) return texture(uTexFb, logoUV);
  if (m == 1) return texture(uTexIg, logoUV);
  return texture(uTexX, logoUV);
}

void main() {
  vec2 uv = (vUV - 0.5) * 2.0;
  uv.x *= uResolution.x / uResolution.y;

  float T = uTime;

  // === Find the two nearest grains ===
  float minD = 1e3;
  float minD2 = 1e3;
  int idx = 0;
  int idx2 = 0;
  vec2 closestPos = vec2(0.0);
  float closestAngle = 0.0;
  float closestCharge = 0.0;

  for (int i = 0; i < N_GRAINS; i++) {
    vec2 gpos;
    float gangle;
    float gcharge;
    grain(i, T, gpos, gangle, gcharge);
    float d = distance(uv, gpos);
    if (d < minD) {
      minD2 = minD;
      idx2 = idx;
      minD = d;
      idx = i;
      closestPos = gpos;
      closestAngle = gangle;
      closestCharge = gcharge;
    } else if (d < minD2) {
      minD2 = d;
      idx2 = i;
    }
  }

  vec2 grain2Pos;
  float grain2Angle;
  float grain2Charge;
  grain(idx2, T, grain2Pos, grain2Angle, grain2Charge);

  float gbDist = (minD2 - minD) * 0.5;

  vec2 latDir = vec2(cos(closestAngle), sin(closestAngle));
  vec2 gridP = uv - closestPos;
  float along = dot(gridP, latDir);
  float across = dot(gridP, vec2(-latDir.y, latDir.x));

  float phase = along * 18.0 - T * 1.5;
  float latticeIntensity = exp(-across * across * 8.0);
  float latticeWave = 0.5 + 0.5 * sin(phase);
  vec3 grainHue = iridescent(float(idx) * 0.137 + T * 0.04);
  vec3 latCol = grainHue * latticeWave * latticeIntensity;

  float inside = smoothstep(0.04, 0.10, gbDist);
  vec3 col = latCol * inside;

  float gbLine = exp(-gbDist * 80.0) * 1.5;
  vec3 gbColor = mix(grainHue, iridescent(float(idx2) * 0.137 + T * 0.04), 0.5);
  col += vec3(1.0) * gbLine * 0.9;
  col += gbColor * gbLine * 0.3;

  float angleDiff = abs(closestAngle - grain2Angle);
  angleDiff = min(angleDiff, TAU - angleDiff);
  float triplePoint = smoothstep(0.5, 1.0, angleDiff) * exp(-gbDist * 30.0);
  col += vec3(0.95, 0.85, 0.65) * triplePoint * 0.8;

  // === Dislocation cores: bright dots, with the LOGO stamped inside ===
  float coreD = minD;
  // Soft bright halo around the core.
  float coreGlow = exp(-coreD * coreD * 1500.0) * 1.5;
  float shockPhase = mod(T * 0.1 + float(idx) * 0.13, 1.0);
  float shockR = shockPhase * 0.4;
  float shockRing = exp(-pow(coreD - shockR, 2.0) * 800.0) * (1.0 - shockPhase);
  float shockwave = shockRing * 1.2;

  vec3 coreCol = vec3(1.0, 0.7, 0.4);
  if (abs(closestCharge) > 0.5) {
    float arg = atan(uv.y - closestPos.y, uv.x - closestPos.x + 1e-6);
    if (closestCharge > 0.5) {
      float rose = 0.5 + 0.5 * cos(arg * 5.0 - T * 0.7);
      coreCol = mix(vec3(1.0, 0.5, 0.3), vec3(0.95, 0.85, 0.65), rose);
    } else {
      float vortex = 0.5 + 0.5 * sin(arg * 3.0 + coreD * 30.0 - T * 1.0);
      coreCol = mix(vec3(0.3, 0.5, 1.0), vec3(0.6, 0.9, 0.95), vortex);
    }
  }
  col += coreCol * coreGlow;
  col += vec3(1.0, 0.95, 0.85) * shockwave;

  // === LOGO RENDER ===
  // Stamp a small logo glyph inside the core. Logo radius in scene
  // units — small but readable. Mask is a smooth disc; outside the
  // disc, no logo is drawn (so the surrounding core glow stays clean).
  {
    // Pre-account for the aspect ratio we applied to uv so the logo
    // isn't stretched horizontally.
    vec2 aspect = vec2(uResolution.x / uResolution.y, 1.0);
    vec2 localUV = (uv - closestPos) / aspect;
    float r = length(localUV);
    float logoR = 0.022;
    if (r < logoR) {
      // Polar coords for rotating the logo with the lattice.
      float ang = atan(localUV.y, localUV.x + 1e-6);
      float rot = closestAngle;
      // Rotate into the logo's local frame, then map to texture UV.
      vec2 rotUV = vec2(
        cos(rot) * localUV.x + sin(rot) * localUV.y,
        -sin(rot) * localUV.x + cos(rot) * localUV.y
      );
      vec2 logoUV = rotUV / logoR * 0.5 + 0.5;
      vec4 logo = sampleLogo(idx, logoUV);
      // Disc mask: hard at center, soft at the rim.
      float disc = smoothstep(logoR, logoR * 0.55, r);
      // Only opaque logo pixels contribute (logo texture has alpha).
      float a = logo.a * disc;
      // Sit the logo on a dark plate so the original core glow doesn't
      // bleed through the glyph's white pixels.
      vec3 plate = vec3(0.06, 0.07, 0.10);
      col = mix(col, plate, a);
      col = mix(col, logo.rgb, a);
    }
  }

  // === Drive all cores: render ALL 36 dislocation cores, not just
  // the closest one. Each has a faint glow plus its shockwave. Logos
  // are only stamped on the closest grain (already handled above) so
  // the rest stay as ambient cores. ===
  for (int i = 0; i < N_GRAINS; i++) {
    vec2 gpos;
    float gangle;
    float gcharge;
    grain(i, T, gpos, gangle, gcharge);
    float d = length(uv - gpos);
    if (d < 0.01) {
      float fcore = exp(-d * d * 10000.0);
      col += vec3(1.0, 0.95, 0.85) * fcore * 0.5;
    }
    if (i != idx) {
      float sp = mod(T * 0.1 + float(i) * 0.13, 1.0);
      float sr = sp * 0.4;
      float sring = exp(-pow(d - sr, 2.0) * 800.0) * (1.0 - sp) * 0.3;
      col += vec3(0.95, 0.85, 0.55) * sring;
    }
  }

  // === Substrate ===
  vec3 substrate = vec3(0.04, 0.06, 0.10);
  float weave1 = 0.5 + 0.5 * sin(uv.x * 200.0 + T * 0.3);
  float weave2 = 0.5 + 0.5 * sin(uv.y * 200.0 - T * 0.3);
  float weave = mix(weave1, weave2, 0.5);
  substrate += vec3(0.01, 0.02, 0.04) * weave;

  float latticeOpacity = 0.3 + 0.7 * inside;
  col = mix(substrate, col, latticeOpacity);

  col *= 0.9 + 0.1 * bass;

  float vig = 1.0 - dot(uv, uv) * 0.25;
  col *= clamp(vig, 0.0, 1.0);

  col = (col * (2.51 * col + 0.03)) / (col * (2.43 * col + 0.59) + 0.14);
  col = pow(clamp(col, 0.0, 1.0), vec3(1.0 / 2.2));

  outColor = vec4(col, 1.0);
}`;

function bands(t: number, o: Float32Array): void {
  for (let i = 0; i < 9; i++) o[i] = 0.5 + 0.3 * Math.sin(t * (0.8 + i * 0.17));
}
export function computeDislocationFieldLogosBands(t: number, o: Float32Array): void { bands(t, o); }
export function computeDislocationFieldLogosRealtimeBands(f: Uint8Array, o: Float32Array): void {
  const N = f.length;
  for (let i = 0; i < 9; i++) {
    const start = Math.floor(i / 9 * N);
    const end = Math.floor((i + 1) / 9 * N);
    let s = 0, n = 0;
    for (let k = start; k < end; k++) { s += f[k] || 0; n++; }
    o[i] = n > 0 ? (s / n) / 255 : 0;
  }
}

/**
 * Bake a 128x128 RGBA canvas with a single logo glyph. Returns a
 * WebGL texture (RGBA, premultiplied not required, NEAREST filter so
 * the glyph stays crisp). Transparent everywhere except the logo
 * silhouette — the shader uses the alpha to mask.
 */
function bakeLogoTexture(
  gl: WebGL2RenderingContext,
  paint: (ctx: CanvasRenderingContext2D, size: number) => void,
): WebGLTexture {
  const size = 128;
  const canvas = document.createElement("canvas");
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext("2d")!;
  ctx.clearRect(0, 0, size, size);
  paint(ctx, size);

  const tex = gl.createTexture()!;
  gl.bindTexture(gl.TEXTURE_2D, tex);
  gl.pixelStorei(gl.UNPACK_PREMULTIPLY_ALPHA_WEBGL, true);
  gl.texImage2D(gl.TEXTURE_2D, 0, gl.RGBA, gl.RGBA, gl.UNSIGNED_BYTE, canvas);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MIN_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_MAG_FILTER, gl.LINEAR);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_S, gl.CLAMP_TO_EDGE);
  gl.texParameteri(gl.TEXTURE_2D, gl.TEXTURE_WRAP_T, gl.CLAMP_TO_EDGE);
  return tex;
}

function paintFacebook(ctx: CanvasRenderingContext2D, size: number): void {
  // Rounded-square blue plate, white "f".
  const s = size;
  const r = s * 0.18;
  const plate = new Path2D();
  plate.moveTo(r, 0);
  plate.lineTo(s - r, 0);
  plate.quadraticCurveTo(s, 0, s, r);
  plate.lineTo(s, s - r);
  plate.quadraticCurveTo(s, s, s - r, s);
  plate.lineTo(r, s);
  plate.quadraticCurveTo(0, s, 0, s - r);
  plate.lineTo(0, r);
  plate.quadraticCurveTo(0, 0, r, 0);
  plate.closePath();
  ctx.fillStyle = "#1877F2";
  ctx.fill(plate);

  // White "f" — bold sans-serif, centered slightly off so the glyph's
  // optical center sits in the disc.
  ctx.fillStyle = "#ffffff";
  ctx.font = "900 92px 'Helvetica Neue', Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.textBaseline = "middle";
  ctx.fillText("f", s * 0.5, s * 0.56);
}

function paintInstagram(ctx: CanvasRenderingContext2D, size: number): void {
  // Gradient square plate (Instagram brand colors) with a stylized
  // camera: rounded rect outline + circle + small dot.
  const s = size;
  // Linear gradient: purple → pink → orange.
  const grad = ctx.createLinearGradient(0, 0, s, s);
  grad.addColorStop(0.0, "#feda75");
  grad.addColorStop(0.25, "#fa7e1e");
  grad.addColorStop(0.55, "#d62976");
  grad.addColorStop(0.85, "#962fbf");
  grad.addColorStop(1.0, "#4f5bd5");
  ctx.fillStyle = grad;
  // Rounded square.
  const r = s * 0.22;
  const plate = new Path2D();
  plate.moveTo(r, 0);
  plate.lineTo(s - r, 0);
  plate.quadraticCurveTo(s, 0, s, r);
  plate.lineTo(s, s - r);
  plate.quadraticCurveTo(s, s, s - r, s);
  plate.lineTo(r, s);
  plate.quadraticCurveTo(0, s, 0, s - r);
  plate.lineTo(0, r);
  plate.quadraticCurveTo(0, 0, r, 0);
  plate.closePath();
  ctx.fill(plate);

  // Camera body: rounded rect outline.
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = s * 0.07;
  const cs = s * 0.28; // half-size of the camera body
  const ccr = s * 0.10; // corner radius
  const cam = new Path2D();
  cam.moveTo(s / 2 - cs + ccr, s / 2 - cs);
  cam.lineTo(s / 2 + cs - ccr, s / 2 - cs);
  cam.quadraticCurveTo(s / 2 + cs, s / 2 - cs, s / 2 + cs, s / 2 - cs + ccr);
  cam.lineTo(s / 2 + cs, s / 2 + cs - ccr);
  cam.quadraticCurveTo(s / 2 + cs, s / 2 + cs, s / 2 + cs - ccr, s / 2 + cs);
  cam.lineTo(s / 2 - cs + ccr, s / 2 + cs);
  cam.quadraticCurveTo(s / 2 - cs, s / 2 + cs, s / 2 - cs, s / 2 + cs - ccr);
  cam.lineTo(s / 2 - cs, s / 2 - cs + ccr);
  cam.quadraticCurveTo(s / 2 - cs, s / 2 - cs, s / 2 - cs + ccr, s / 2 - cs);
  cam.closePath();
  ctx.stroke(cam);

  // Lens circle.
  ctx.beginPath();
  ctx.arc(s / 2, s / 2, s * 0.13, 0, Math.PI * 2);
  ctx.stroke();

  // Flash dot, top-right of camera.
  ctx.beginPath();
  ctx.arc(s / 2 + cs * 0.6, s / 2 - cs * 0.6, s * 0.035, 0, Math.PI * 2);
  ctx.fillStyle = "#ffffff";
  ctx.fill();
}

function paintX(ctx: CanvasRenderingContext2D, size: number): void {
  // Black rounded square plate with white X.
  const s = size;
  const r = s * 0.18;
  const plate = new Path2D();
  plate.moveTo(r, 0);
  plate.lineTo(s - r, 0);
  plate.quadraticCurveTo(s, 0, s, r);
  plate.lineTo(s, s - r);
  plate.quadraticCurveTo(s, s, s - r, s);
  plate.lineTo(r, s);
  plate.quadraticCurveTo(0, s, 0, s - r);
  plate.lineTo(0, r);
  plate.quadraticCurveTo(0, 0, r, 0);
  plate.closePath();
  ctx.fillStyle = "#000000";
  ctx.fill(plate);

  // White "X" — drawn as two rotated strokes for crispness at any
  // raster size (text can be sub-pixel-blurry on some browsers).
  ctx.strokeStyle = "#ffffff";
  ctx.lineWidth = s * 0.11;
  ctx.lineCap = "round";
  const m = s * 0.28;
  ctx.beginPath();
  ctx.moveTo(m, m);
  ctx.lineTo(s - m, s - m);
  ctx.moveTo(s - m, m);
  ctx.lineTo(m, s - m);
  ctx.stroke();
}

export function createDislocationFieldLogosPipeline(gl: WebGL2RenderingContext): DislocationFieldLogosPipeline | null {
  const program = linkProgram(gl, VERT_SRC, FRAG_SRC, POST_ATTRIBS);
  if (!program) return null;
  const vao = gl.createVertexArray();
  const vbo = gl.createBuffer();
  if (!vao || !vbo) return null;
  gl.bindVertexArray(vao);
  gl.bindBuffer(gl.ARRAY_BUFFER, vbo);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1,-1, 1,-1, -1,1, -1,1, 1,-1, 1,1]), gl.STATIC_DRAW);
  gl.enableVertexAttribArray(0);
  gl.vertexAttribPointer(0, 2, gl.FLOAT, false, 0, 0);
  gl.bindVertexArray(null);

  const texFb = bakeLogoTexture(gl, paintFacebook);
  const texIg = bakeLogoTexture(gl, paintInstagram);
  const texX  = bakeLogoTexture(gl, paintX);

  return { program, vao, vbo, texFb, texIg, texX, bands: new Float32Array(9) };
}

export function drawDislocationFieldLogos(
  gl: WebGL2RenderingContext,
  p: DislocationFieldLogosPipeline,
  w: number, h: number, t: number,
): void {
  gl.useProgram(p.program);
  const prog = p.program;
  gl.uniform2f(gl.getUniformLocation(prog, "uResolution"), w, h);
  gl.uniform1f(gl.getUniformLocation(prog, "uTime"), t);
  gl.uniform3f(gl.getUniformLocation(prog, "uBass"), p.bands[0], p.bands[1], p.bands[2]);
  gl.uniform3f(gl.getUniformLocation(prog, "uMid"), p.bands[3], p.bands[4], p.bands[5]);
  gl.uniform3f(gl.getUniformLocation(prog, "uHigh"), p.bands[6], p.bands[7], p.bands[8]);

  // Bind the three logo textures to fixed units 0/1/2.
  gl.activeTexture(gl.TEXTURE0);
  gl.bindTexture(gl.TEXTURE_2D, p.texFb);
  gl.uniform1i(gl.getUniformLocation(prog, "uTexFb"), 0);
  gl.activeTexture(gl.TEXTURE1);
  gl.bindTexture(gl.TEXTURE_2D, p.texIg);
  gl.uniform1i(gl.getUniformLocation(prog, "uTexIg"), 1);
  gl.activeTexture(gl.TEXTURE2);
  gl.bindTexture(gl.TEXTURE_2D, p.texX);
  gl.uniform1i(gl.getUniformLocation(prog, "uTexX"), 2);

  gl.bindVertexArray(p.vao);
  gl.drawArrays(gl.TRIANGLES, 0, 6);
  gl.bindVertexArray(null);
}

export { VERT_SRC, FRAG_SRC };
